// src/features/messages/ConversationThread.tsx — active conversation pane.
import { useState } from 'react';
import styled from 'styled-components';
import { Avatar, Button, DisabledHint, IconButton, MetaRow } from '@/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MessageBubble } from './MessageBubble';
import { Composer } from './Composer';
import { sendTyping } from '@/core/lib/realtime';
import { useTypingIndicator } from '@/core/stores/typingStore';
import { useOnlineOverride } from '@/core/stores/presenceStore';
import type { Conversation } from '@/core/models/message';
import type { OutgoingMessageImage } from '@/core/services/messagesService';

const Pane = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  height: 100%;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const Title = styled.div`
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px;
`;
const Name = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Sub = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Actions = styled.div`display: flex; gap: 4px;`;
const Scroll = styled.div`
  flex: 1; min-height: 0; overflow-y: auto;
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const Empty = styled.div`
  flex: 1; display: grid; place-items: center; padding: 28px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const TypingLine = styled.div`
  padding: 0 14px 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;
const RequestBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
`;
const RequestText = styled.p`
  flex: 1;
  min-width: 200px;
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const RequestActions = styled.div`display: flex; gap: 8px;`;
const RequestError = styled.span`
  flex-basis: 100%;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;
const AwaitingBanner = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface ConversationThreadProps {
  convo: Conversation | null;
  onSend: (text: string, image?: OutgoingMessageImage) => void | Promise<void>;
  /** Mobile only: clears the active conversation, returning to the list. */
  onBack?: () => void;
  /** Mobile only: opens the chat-info bottom Sheet. */
  onChatInfo?: () => void;
  /** Accept this message request (only reachable while `convo.pending`). */
  onAccept?: (id: string) => void | Promise<void>;
  /** Decline this message request — deletes the thread for both sides. */
  onDecline?: (id: string) => void | Promise<void>;
  /**
   * Whether the viewer's email is confirmed. Accept/decline are email-gated
   * server-side (same rule as responding to a group invite), so an unconfirmed
   * member gets a hinted control rather than a button that 403s. Defaults to
   * true: an unknown state must not lock anyone out.
   */
  emailVerified?: boolean;
}

export function ConversationThread({
  convo,
  onSend,
  onBack,
  onChatInfo,
  onAccept,
  onDecline,
  emailVerified = true,
}: ConversationThreadProps) {
  // Hooks must run unconditionally — read the indicator for an empty id when
  // there's no active conversation; typingStore simply never has that entry.
  const typingHandle = useTypingIndicator(convo?.id ?? '');
  // Same live-override-over-REST pattern as ConversationRow — see there.
  const liveOnline = useOnlineOverride(convo?.otherUserId ?? undefined);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [respondBusy, setRespondBusy] = useState(false);
  const [respondFailed, setRespondFailed] = useState(false);

  if (!convo) {
    return (
      <Pane>
        <Empty>Select a conversation to start chatting.</Empty>
      </Pane>
    );
  }

  const memberCount = convo.members ?? 0;
  const pendingCount = convo.pendingInvites?.length ?? 0;
  // 1.24.00: on a DM, `null` means the other side (or this viewer) turned
  // Activity status off. That is NOT the same as being offline, so the header
  // shows neither "Online" nor "Offline" — it shows nothing at all. An "Offline"
  // label would still be reporting on someone who asked not to be reported on.
  //
  // Scoped to DMs on purpose: the server sends `online: null` on every GROUP
  // too, where it means "not applicable" rather than "hidden". Reading that as
  // hidden killed the "is typing…" line in every group chat there is.
  const presenceHidden = convo.type === 'dm' && convo.online === null;
  const online = presenceHidden ? null : (liveOnline ?? convo.online);
  const sub =
    convo.type === 'group'
      ? `${memberCount} ${memberCount === 1 ? 'member' : 'members'}`
      : presenceHidden
        ? ''
        : online
          ? 'Online'
          : 'Offline';
  const invited = convo.type === 'group' && pendingCount > 0 ? `${pendingCount} invited` : '';

  const isRequest = convo.pending === true;
  const awaiting = convo.awaitingAcceptance === true;
  // The typing line is presence too — hide it under the same switch.
  const showTyping = typingHandle !== null && !presenceHidden;

  // One "Seen" line per thread: the last message I sent.
  const lastOwnId = [...convo.messages].reverse().find((m) => m.fromMe)?.id ?? null;

  // Accept/decline are pessimistic and report their own failure inline —
  // mirroring GroupInvitesBlock, the other accept/decline surface on this page.
  const convoId = convo.id;
  async function runRespond(fn: ((id: string) => void | Promise<void>) | undefined) {
    if (!fn) return;
    setRespondBusy(true);
    setRespondFailed(false);
    try {
      await fn(convoId);
    } catch {
      setRespondFailed(true);
    } finally {
      setRespondBusy(false);
    }
  }

  const respondBlockedReason = emailVerified
    ? null
    : 'Confirm your email address first. Check your inbox for the link.';

  return (
    <Pane>
      <Header>
        {onBack && (
          <IconButton icon="arrow-left" label="Back to conversations" onClick={onBack} />
        )}
        <Avatar name={convo.name} size={40} />
        <Title>
          <Name>{convo.name}</Name>
          {sub !== '' && (
            <Sub>
              <MetaRow>
                {sub}
                {invited}
              </MetaRow>
            </Sub>
          )}
        </Title>
        <Actions>
          {/* Calling does not exist on TruXSocial and is not being built — the
              honest state is a hinted control, not a button that swallows the
              press silently. */}
          <DisabledHint explanation="Voice and video calls aren't available yet.">
            {(describedBy) => (
              <IconButton
                icon="phone"
                label="Start call (unavailable)"
                aria-disabled="true"
                aria-describedby={describedBy}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            )}
          </DisabledHint>
          <DisabledHint explanation="Voice and video calls aren't available yet.">
            {(describedBy) => (
              <IconButton
                icon="video"
                label="Start video call (unavailable)"
                aria-disabled="true"
                aria-describedby={describedBy}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            )}
          </DisabledHint>
          <IconButton
            icon="info"
            label="Conversation info"
            onClick={onChatInfo}
          />
        </Actions>
      </Header>
      <Scroll>
        {convo.messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            isGroup={convo.type === 'group'}
            otherReadAt={convo.otherReadAt ?? null}
            showReceipt={m.id === lastOwnId}
          />
        ))}
      </Scroll>
      {showTyping && (
        <TypingLine aria-live="polite">{typingHandle} is typing…</TypingLine>
      )}
      {awaiting && (
        <AwaitingBanner role="status">
          Request sent. They&apos;ll see it in Requests.
        </AwaitingBanner>
      )}
      {isRequest && (
        <RequestBar>
          <RequestText>
            {convo.name} wants to message you. Accepting moves this into your inbox.
          </RequestText>
          {respondBlockedReason ? (
            <RequestActions>
              {(['Decline', 'Accept'] as const).map((label) => (
                <DisabledHint key={label} explanation={respondBlockedReason}>
                  {(describedBy) => (
                    <Button
                      variant={label === 'Accept' ? 'primary' : 'secondary'}
                      aria-disabled="true"
                      aria-describedby={describedBy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {label}
                    </Button>
                  )}
                </DisabledHint>
              ))}
            </RequestActions>
          ) : (
            <RequestActions>
              <Button
                variant="secondary"
                disabled={respondBusy}
                onClick={() => setConfirmDecline(true)}
              >
                Decline
              </Button>
              <Button
                variant="primary"
                disabled={respondBusy}
                onClick={() => void runRespond(onAccept)}
              >
                Accept
              </Button>
            </RequestActions>
          )}
          {respondFailed && (
            <RequestError role="alert">
              Couldn&apos;t do that. Please try again.
            </RequestError>
          )}
        </RequestBar>
      )}
      <Composer
        onSend={onSend}
        onTyping={() => sendTyping(convo.id)}
        {...(isRequest ? { disabledReason: 'Accept the request to reply.' } : {})}
      />
      {confirmDecline && (
        <ConfirmDialog
          title="Decline this request?"
          message={`Declining deletes this conversation for both you and ${convo.name}. The messages are gone for good, and they are not told.`}
          confirmLabel="Decline"
          onConfirm={() => {
            setConfirmDecline(false);
            void runRespond(onDecline);
          }}
          onClose={() => setConfirmDecline(false)}
        />
      )}
    </Pane>
  );
}
